import { format } from 'date-fns';
import { randomString } from './common';

export const DASHBOARD_TABS = [
    'White Card',
    'Traffic Control',
    'Traffic Implement',
    'Qld',
];

export const QLD_TAB = 'Qld';

export const ADMIN_CLASS_TYPE_CONFIG = {
    whiteCard: {
        key: 'whiteCard',
        label: 'NSW - White Card',
        courseLabel: 'White Card',
        tabs: ['White Card'],
    },
    trafficControl: {
        key: 'trafficControl',
        label: 'Traffic Control',
        courseLabel: 'Traffic Control',
        tabs: ['Traffic Control'],
    },
    trafficImplement: {
        key: 'trafficImplement',
        label: 'Traffic Implement',
        courseLabel: 'Traffic Implement',
        tabs: ['Traffic Implement'],
    },
    qldWhiteCard: {
        key: 'qldWhiteCard',
        label: 'QLD White Card',
        courseLabel: 'QLD White Card',
        tabs: ['Qld'],
    },
};

export const getAdminClassTypeForTab = (tab = '') => (
    Object.values(ADMIN_CLASS_TYPE_CONFIG).find((config) => config.tabs.includes(tab)) || null
);

export const getAdminClassTypeKeyForTab = (tab = '') => getAdminClassTypeForTab(tab)?.key || '';

// Every entry in createCourseOptions states its courseType, which names the course
// exactly. Matching that is reliable; matching the class name is not, because
// "Implement Traffic Control Plans Course" contains "Traffic Control" and so reads as the
// Control course unless the order is just so. The name is only a fallback, for classes
// created before courseType was recorded.
export const COURSE_TYPE_CLASS_KEYS = Object.freeze({
    'Traffic Controller': ADMIN_CLASS_TYPE_CONFIG.trafficControl.key,
    'Implement Traffic Control Plans': ADMIN_CLASS_TYPE_CONFIG.trafficImplement.key,
});

// 'TCT Course' is a course day, 'TCT Assessment' is the practical that follows it. Storing
// which one a class is lets a booking be matched to its course day rather than its
// assessment, and the other way round for a standalone practical booking.
export const ADMIN_CLASS_KINDS = Object.freeze({ course: 'course', assessment: 'assessment' });

export const getAdminClassKind = (course = '') => (
    String(course).includes('Assessment') ? ADMIN_CLASS_KINDS.assessment : ADMIN_CLASS_KINDS.course
);

export const getAdminClassTypeFromCreateCourse = (courseName = '', courseType = '') => {
    // The exact course type wins whenever the caller has it.
    if (courseType && COURSE_TYPE_CLASS_KEYS[courseType]) {
        return COURSE_TYPE_CLASS_KEYS[courseType];
    }

    if (!courseName) {
        return '';
    }

    if (courseName.includes('White Card')) {
        return ADMIN_CLASS_TYPE_CONFIG.whiteCard.key;
    }

    // Implement is tested before Control because "Implement Traffic Control Plans Course"
    // contains "Traffic Control". With Control first it claimed that name and this branch
    // was unreachable for it, so the Implement course days were filed as Traffic Control
    // and appeared under the wrong dashboard tab.
    if (courseName.includes('Traffic Implement') || courseName.includes('Implement Traffic Control Plans')) {
        return ADMIN_CLASS_TYPE_CONFIG.trafficImplement.key;
    }

    if (courseName.includes('Traffic Control')) {
        return ADMIN_CLASS_TYPE_CONFIG.trafficControl.key;
    }

    return '';
};

export const buildAdminClassDocument = ({
    courseName = '',
    courseType = '',
    course = '',
    trainerId = '',
    trainerName = '',
    scheduledDate,
    startTime = '',
    endTime = '',
    delivery = '',
    location = '',
    onlineLink = '',
    source = 'manual',
}) => {
    const courseTypeKey = getAdminClassTypeFromCreateCourse(courseName, courseType);
    const classKind = getAdminClassKind(course);
    const config = ADMIN_CLASS_TYPE_CONFIG[courseTypeKey];
    const classDate = scheduledDate ? new Date(scheduledDate) : new Date();
    const scheduledDateKey = format(classDate, 'yyyy-MM-dd');

    return {
        classId: `${courseTypeKey || 'class'}-${scheduledDateKey}-${trainerId || 'trainer'}-${randomString(6)}`,
        courseTypeKey,
        courseName,
        courseType,
        classKind,
        courseLabel: config?.courseLabel || courseName,
        trainerId,
        trainerName,
        scheduledDate: classDate.toISOString(),
        scheduledDateKey,
        displayDate: format(classDate, 'dd MMM yyyy'),
        startTime,
        endTime,
        delivery,
        location,
        onlineLink,
        source,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
};

const normalizeTrainerId = (value = '') => String(value || '').trim().toLowerCase();
const normalizeDateKey = (value) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return format(date, 'yyyy-MM-dd');
};

export const deriveLegacyClassGroup = (row, currentTab) => {
    const classType = getAdminClassTypeForTab(currentTab);
    const createdAt = row?.Created ? new Date(row.Created * 1000) : new Date();
    const scheduledDateKey = normalizeDateKey(createdAt);
    const trainerId = normalizeTrainerId(row?.trainerId || row?.data?.trainerId || '');
    const delivery = row?.delivery || row?.data?.delivery || '';
    const groupKey = [
        classType?.key || 'legacy',
        trainerId || 'trainer',
        scheduledDateKey || 'date',
        delivery || 'delivery',
    ].join('__');

    return {
        id: `legacy-${groupKey}`,
        classId: '',
        groupKey,
        isDerived: true,
        courseTypeKey: classType?.key || '',
        courseLabel: classType?.courseLabel || currentTab,
        trainerId,
        trainerName: '',
        scheduledDate: createdAt.toISOString(),
        scheduledDateKey,
        displayDate: format(createdAt, 'dd MMM yyyy'),
        delivery,
        studentCount: 0,
        rows: [],
    };
};

export const matchesRowToAdminClass = (row, adminClass, currentTab) => {
    if (!row || !adminClass) {
        return false;
    }

    const rowClassId = row.classId || row.data?.classId;
    if (rowClassId) {
        return rowClassId === (adminClass.classId || adminClass.id);
    }

    const classType = getAdminClassTypeForTab(currentTab);
    const rowTrainerId = normalizeTrainerId(row?.trainerId || row?.data?.trainerId || '');
    const rowDateKey = row.classDate || normalizeDateKey(row?.Created ? row.Created * 1000 : row?.data?.dateFirstPage || row?.createdAt);
    const classTrainerId = normalizeTrainerId(adminClass.trainerId || '');
    const classDateKey = adminClass.scheduledDateKey || normalizeDateKey(adminClass.scheduledDate);

    return (
        classType?.key === adminClass.courseTypeKey
        && rowTrainerId === classTrainerId
        && rowDateKey === classDateKey
        && String(row.delivery || row.data?.delivery || '').toLowerCase() === String(adminClass.delivery || '').toLowerCase()
    );
};

// The containing class supplies scope, not the individual record's optional
// classId. EOIs created before class linking must pair with newer declarations.
export const getClassStudentKey = (row, classGroupId = '') => JSON.stringify([
        classGroupId,
        String(row?.firstName || row?.data?.firstName || '').trim().toLowerCase(),
        String(row?.lastName || row?.data?.lastName || '').trim().toLowerCase(),
        String(row?.dob || row?.data?.dob || '').trim().toLowerCase(),
]);

export const buildAdminClassGroups = ({ rows = [], classes = [], currentTab }) => {
    const getStudentKey = row => getClassStudentKey(row);
    const mergeGroupRows = (groupsToMerge) => {
        const mergedRows = groupsToMerge.flatMap((group) => group.rows || []);
        const uniqueStudentCount = new Set(mergedRows.map(getStudentKey)).size;
        const sortedByDate = [...groupsToMerge].sort((left, right) => (
            new Date(left.scheduledDate || left.createdAt || 0).getTime() - new Date(right.scheduledDate || right.createdAt || 0).getTime()
        ));
        const baseGroup = sortedByDate[0];

        return {
            ...baseGroup,
            rows: mergedRows,
            studentCount: uniqueStudentCount,
            scheduledDate: baseGroup.scheduledDate,
            scheduledDateKey: baseGroup.scheduledDateKey,
            displayDate: baseGroup.displayDate,
            isDerived: true,
        };
    };

    const groupedRows = rows.reduce((acc, row) => {
        const matchingClass = classes.find((classRow) => matchesRowToAdminClass(row, classRow, currentTab));
        if (matchingClass) {
            const nextRows = [...(acc[matchingClass.id]?.rows || []), row];
            const uniqueStudentCount = new Set(nextRows.map(getStudentKey)).size;
            acc[matchingClass.id] = {
                ...matchingClass,
                id: matchingClass.id,
                rows: nextRows,
                studentCount: uniqueStudentCount,
                isDerived: false,
            };
            return acc;
        }

        const derived = deriveLegacyClassGroup(row, currentTab);
        const nextRows = [...(acc[derived.id]?.rows || []), row];
        const uniqueStudentCount = new Set(nextRows.map(getStudentKey)).size;
        acc[derived.id] = {
            ...derived,
            rows: nextRows,
            studentCount: uniqueStudentCount,
        };
        return acc;
    }, {});

    const emptyStoredClasses = classes.reduce((acc, classRow) => {
        if (groupedRows[classRow.id]) {
            return acc;
        }

        acc[classRow.id] = {
            ...classRow,
            rows: [],
            studentCount: 0,
            isDerived: false,
        };
        return acc;
    }, {});

    const combinedGroups = [...Object.values(groupedRows), ...Object.values(emptyStoredClasses)];
    const storedGroups = combinedGroups.filter((group) => !group.isDerived);
    const derivedGroups = combinedGroups.filter((group) => group.isDerived);
    const mergedDerivedGroups = [];

    derivedGroups.forEach((group) => {
        const matchingMergedIndex = mergedDerivedGroups.findIndex((existingGroup) => (
            existingGroup.courseTypeKey === group.courseTypeKey
            && normalizeTrainerId(existingGroup.trainerId) === normalizeTrainerId(group.trainerId)
            && existingGroup.scheduledDateKey === group.scheduledDateKey
        ));

        if (matchingMergedIndex === -1) {
            mergedDerivedGroups.push(group);
            return;
        }

        mergedDerivedGroups[matchingMergedIndex] = mergeGroupRows([
            mergedDerivedGroups[matchingMergedIndex],
            group,
        ]);
    });

    return [...storedGroups, ...mergedDerivedGroups].sort((left, right) => {
        const leftTime = new Date(left.scheduledDate || left.createdAt || 0).getTime();
        const rightTime = new Date(right.scheduledDate || right.createdAt || 0).getTime();
        return rightTime - leftTime;
    });
};
